import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import ThemeToggle from "../components/ThemeToggle";
import BrandLogo from "../components/BrandLogo";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, setUser } = useAuth();
  const { theme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await login(email, password);
      toast.success("Welcome back!");
      
      if (data.user.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/app");
      }
    } catch (error) {
      toast.error(error.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    const redirectUrl = window.location.origin + "/app";
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    
    const popup = window.open(
      authUrl,
      "Google Sign In",
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    const checkPopup = setInterval(async () => {
      try {
        if (popup.closed) {
          clearInterval(checkPopup);
          const response = await fetch(`${API_URL}/api/auth/me`, {
            credentials: "include",
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            localStorage.setItem("user", JSON.stringify(userData));
            toast.success("Welcome!");
            if (userData.role === "admin") {
              navigate("/admin");
            } else {
              navigate("/app");
            }
          }
        } else if (popup.location.href.includes(window.location.origin)) {
          const hash = popup.location.hash;
          if (hash.includes("session_id=")) {
            popup.close();
            clearInterval(checkPopup);
            
            const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
            
            const response = await fetch(`${API_URL}/api/auth/session`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ session_id: sessionId }),
            });
            
            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
              localStorage.setItem("user", JSON.stringify(userData));
              toast.success("Welcome!");
              if (userData.role === "admin") {
                navigate("/admin");
              } else {
                navigate("/app");
              }
            }
          }
        }
      } catch (e) {}
    }, 500);

    setTimeout(() => clearInterval(checkPopup), 300000);
  };

  return (
    <div className={`min-h-screen flex transition-colors duration-300 ${
      theme === 'dark' ? 'bg-[#011a3a]' : 'bg-[#f8fafc]'
    }`}>
      {/* Theme toggle */}
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center mb-8">
            <BrandLogo className="h-8 w-auto" />
          </Link>

          <h1 className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-[#011a3a]'}`}>
            Welcome back
          </h1>
          <p className={`mb-8 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
            Sign in to your affiliate account
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className={theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="login-email"
                  className={`pl-10 ${
                    theme === 'dark' 
                      ? 'bg-[#012169] border-white/10 text-white placeholder:text-slate-500' 
                      : 'bg-white border-slate-300 text-[#011a3a] placeholder:text-slate-400'
                  }`}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className={theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                  className={`pl-10 ${
                    theme === 'dark' 
                      ? 'bg-[#012169] border-white/10 text-white placeholder:text-slate-500' 
                      : 'bg-white border-slate-300 text-[#011a3a] placeholder:text-slate-400'
                  }`}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Sign In <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className={`flex-1 h-px ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`} />
            <span className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>or</span>
            <div className={`flex-1 h-px ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`} />
          </div>

          <Button
            variant="outline"
            onClick={handleGoogleLogin}
            data-testid="google-login"
            className={`w-full ${
              theme === 'dark' 
                ? 'border-white/10 hover:bg-white/5 text-white' 
                : 'border-slate-300 hover:bg-slate-50 text-[#011a3a]'
            }`}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </Button>

          <p className={`mt-8 text-center text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
            Don't have an account?{" "}
            <Link to="/signup" className="text-blue-500 hover:text-blue-400 font-medium">
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image/Pattern */}
      <div className={`hidden lg:flex flex-1 items-center justify-center border-l ${
        theme === 'dark' 
          ? 'bg-gradient-to-br from-blue-500/10 via-[#1a2744] to-transparent border-white/5' 
          : 'bg-gradient-to-br from-blue-50 via-white to-transparent border-slate-200'
      }`}>
        <div className="max-w-md text-center px-8">
          <div className="mx-auto mb-8">
            <BrandLogo showMark className="w-20 h-20 mx-auto" />
          </div>
          <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-[#011a3a]'}`}>
            Grow with Maaly Pay
          </h2>
          <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}>
            Track your referrals, monitor conversions, and earn commissions - all in one powerful dashboard.
          </p>
        </div>
      </div>
    </div>
  );
}
